import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, CheckCircle2, Send, Loader2, Search } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYEE_OPTIONS = ["10–50", "50–200", "200+"];
const CATEGORY_OPTIONS = ["Chips", "Sodas", "Energy drinks", "Deli sandwiches", "Protein bars"];

const NOVA_CITIES = [
    "tysons", "reston", "arlington", "alexandria", "fairfax", "herndon", "vienna", "mclean",
    "falls church", "ashburn", "leesburg", "sterling", "chantilly", "centreville", "manassas",
    "springfield", "annandale", "burke", "oakton", "great falls", "dulles", "loudoun",
];
const NOVA_ZIP_PREFIXES = ["201", "220", "221", "222", "223"];

const inputCls =
    "w-full rounded-xl border border-[#1B4D1B]/15 bg-white px-4 py-3 text-sm text-[#0E1E12] placeholder:text-[#768578]/60 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#3FAE4A] focus:shadow-[0_0_0_4px_rgba(63,174,74,0.12)]";

function EligibilityChecker() {
    const [query, setQuery] = useState("");
    const [result, setResult] = useState(null);

    const check = () => {
        const q = query.trim().toLowerCase();
        if (!q) return;
        const zipHit = NOVA_ZIP_PREFIXES.some((p) => q.startsWith(p));
        const cityHit = NOVA_CITIES.some((c) => q.includes(c));
        setResult(zipHit || cityHit ? "yes" : "maybe");
    };

    return (
        <div
            data-testid="nova-eligibility-checker"
            className="rounded-3xl border border-[#1B4D1B]/10 bg-white p-7 shadow-[0_20px_50px_-24px_rgba(14,30,18,0.2)]"
        >
            <p className="font-mono-brand text-[11px] tracking-[0.25em] uppercase text-[#3FAE4A] mb-3">
                Service area check
            </p>
            <p className="text-sm text-[#4A584C] mb-4">
                Enter your city or ZIP to confirm same-week installation eligibility.
            </p>
            <div className="flex gap-2">
                <input
                    data-testid="nova-checker-input"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setResult(null);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && check()}
                    placeholder="e.g. Reston or 20190"
                    className={inputCls}
                />
                <button
                    data-testid="nova-checker-btn"
                    onClick={check}
                    className="shrink-0 h-[46px] w-[46px] rounded-xl bg-[#0E1E12] text-white flex items-center justify-center transition-colors duration-300 hover:bg-[#3FAE4A]"
                    aria-label="Check eligibility"
                >
                    <Search className="h-4 w-4" />
                </button>
            </div>
            {result === "yes" && (
                <motion.p
                    data-testid="nova-checker-result-yes"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 flex items-start gap-2 text-sm font-medium text-[#1B4D1B]"
                >
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#3FAE4A] shrink-0" />
                    You're in our core zone — installation typically within 48 hours of approval.
                </motion.p>
            )}
            {result === "maybe" && (
                <motion.p
                    data-testid="nova-checker-result-maybe"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 flex items-start gap-2 text-sm font-medium text-[#4A584C]"
                >
                    <MapPin className="h-4 w-4 mt-0.5 text-[#F58220] shrink-0" />
                    Just outside our core zone — submit the form anyway, we expand routes monthly.
                </motion.p>
            )}
        </div>
    );
}

export default function ContactSection() {
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "", website: "" });
    const [employees, setEmployees] = useState("10–50");
    const [categories, setCategories] = useState([]);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const toggleCategory = (c) =>
        setCategories((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/contact`, { ...form, employees, categories });
            setSent(true);
            toast.success("Request received — we'll reach out within one business day.");
        } catch (err) {
            toast.error("Something went wrong sending your request. Please call 571-281-1784.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 overflow-hidden">
            <div className="absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full bg-[#A6CE39]/20 blur-[120px] pointer-events-none" aria-hidden />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-5 space-y-8">
                        <div>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, ease: EASE }}
                                className="font-mono-brand text-xs tracking-[0.25em] uppercase text-[#3FAE4A] mb-5"
                                data-testid="contact-eyebrow"
                            >
                                Free placement
                            </motion.p>
                            <motion.h2
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                                className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-[#0E1E12]"
                                data-testid="contact-heading"
                            >
                                Get your machine<span className="text-[#F58220]">.</span>
                            </motion.h2>
                            <motion.p
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                                className="mt-5 text-base text-[#4A584C] leading-relaxed"
                                data-testid="contact-subcopy"
                            >
                                Tell us about your office and we'll call you back within one business
                                day to schedule a free site visit. Prefer to talk first?
                            </motion.p>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
                            className="space-y-4"
                            data-testid="contact-direct-info"
                        >
                            <a href="tel:+15712811784" data-testid="contact-phone-link" className="flex items-center gap-4 group">
                                <span className="h-11 w-11 rounded-2xl bg-[#0E1E12] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#F58220]">
                                    <Phone className="h-5 w-5 text-[#A6CE39]" />
                                </span>
                                <span>
                                    <span className="block text-xs uppercase tracking-[0.15em] text-[#768578]">Call us</span>
                                    <span className="block font-semibold text-[#0E1E12]">571-281-1784</span>
                                </span>
                            </a>
                            <a href="mailto:aleassear@crisislogic.com" data-testid="contact-email-link" className="flex items-center gap-4 group">
                                <span className="h-11 w-11 rounded-2xl bg-[#0E1E12] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#F58220]">
                                    <Mail className="h-5 w-5 text-[#A6CE39]" />
                                </span>
                                <span>
                                    <span className="block text-xs uppercase tracking-[0.15em] text-[#768578]">Email</span>
                                    <span className="block font-semibold text-[#0E1E12]">aleassear@crisislogic.com</span>
                                </span>
                            </a>
                            <div className="flex items-center gap-4" data-testid="contact-area-info">
                                <span className="h-11 w-11 rounded-2xl bg-[#0E1E12] flex items-center justify-center">
                                    <MapPin className="h-5 w-5 text-[#A6CE39]" />
                                </span>
                                <span>
                                    <span className="block text-xs uppercase tracking-[0.15em] text-[#768578]">Servicing</span>
                                    <span className="block font-semibold text-[#0E1E12]">Northern Virginia</span>
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
                        >
                            <EligibilityChecker />
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                        className="lg:col-span-7"
                    >
                        <div className="rounded-3xl border border-[#1B4D1B]/10 bg-white p-7 sm:p-10 shadow-[0_30px_80px_-30px_rgba(14,30,18,0.3)]">
                            {sent ? (
                                <motion.div
                                    data-testid="contact-success-state"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.6, ease: EASE }}
                                    className="py-16 text-center"
                                >
                                    <span className="mx-auto mb-6 h-16 w-16 rounded-full bg-[#A6CE39]/15 flex items-center justify-center">
                                        <CheckCircle2 className="h-8 w-8 text-[#3FAE4A]" />
                                    </span>
                                    <h3 className="font-display text-2xl font-bold text-[#0E1E12]">
                                        Request received
                                    </h3>
                                    <p className="mt-3 text-sm text-[#4A584C] max-w-sm mx-auto leading-relaxed">
                                        Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""} — a confirmation
                                        is on its way to your inbox. We'll call {form.company || "your office"} within
                                        one business day.
                                    </p>
                                    <button
                                        data-testid="contact-send-another-btn"
                                        onClick={() => setSent(false)}
                                        className="mt-8 text-sm font-semibold text-[#3FAE4A] hover:text-[#0E1E12] transition-colors duration-300"
                                    >
                                        Submit another request
                                    </button>
                                </motion.div>
                            ) : (
                                <form onSubmit={submit} data-testid="free-placement-contact-form" className="space-y-6">
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div>
                                            <label htmlFor="lead-name" className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#4A584C] mb-2">
                                                Your name *
                                            </label>
                                            <input id="lead-name" data-testid="form-name-input" required value={form.name} onChange={set("name")} placeholder="Alex Leassear" className={inputCls} />
                                        </div>
                                        <div>
                                            <label htmlFor="lead-company" className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#4A584C] mb-2">
                                                Company *
                                            </label>
                                            <input id="lead-company" data-testid="form-company-input" required value={form.company} onChange={set("company")} placeholder="Acme Corp" className={inputCls} />
                                        </div>
                                        <div>
                                            <label htmlFor="lead-email" className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#4A584C] mb-2">
                                                Work email *
                                            </label>
                                            <input id="lead-email" data-testid="form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                                        </div>
                                        <div>
                                            <label htmlFor="lead-phone" className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#4A584C] mb-2">
                                                Phone
                                            </label>
                                            <input id="lead-phone" data-testid="form-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="571-555-0100" className={inputCls} />
                                        </div>
                                    </div>

                                    <div>
                                        <p className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#4A584C] mb-3">
                                            Team size
                                        </p>
                                        <div className="flex flex-wrap gap-2" data-testid="form-employees-group">
                                            {EMPLOYEE_OPTIONS.map((opt) => (
                                                <button
                                                    type="button"
                                                    key={opt}
                                                    data-testid={`form-employees-${opt.replace(/[^0-9a-z]+/gi, "-").toLowerCase()}`}
                                                    onClick={() => setEmployees(opt)}
                                                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                                                        employees === opt
                                                            ? "bg-[#0E1E12] text-white"
                                                            : "bg-[#F1EFE6] text-[#4A584C] hover:bg-[#A6CE39]/25 hover:text-[#0E1E12]"
                                                    }`}
                                                >
                                                    {opt} people
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <p className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#4A584C] mb-3">
                                            What should we stock?
                                        </p>
                                        <div className="flex flex-wrap gap-2" data-testid="form-categories-group">
                                            {CATEGORY_OPTIONS.map((opt) => {
                                                const active = categories.includes(opt);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={opt}
                                                        data-testid={`form-category-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                        onClick={() => toggleCategory(opt)}
                                                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-300 ${
                                                            active
                                                                ? "bg-[#A6CE39]/20 border-[#3FAE4A] text-[#1B4D1B]"
                                                                : "bg-white border-[#1B4D1B]/15 text-[#4A584C] hover:border-[#3FAE4A]/50"
                                                        }`}
                                                    >
                                                        {opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="lead-message" className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#4A584C] mb-2">
                                            Anything else?
                                        </label>
                                        <textarea
                                            id="lead-message"
                                            data-testid="form-message-input"
                                            rows={3}
                                            value={form.message}
                                            onChange={set("message")}
                                            placeholder="Breakroom size, dietary needs, preferred install timing…"
                                            className={`${inputCls} resize-none`}
                                        />
                                    </div>

                                    <input
                                        type="text"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        aria-hidden="true"
                                        className="hp-field"
                                        value={form.website}
                                        onChange={set("website")}
                                        data-testid="form-honeypot"
                                    />

                                    <button
                                        type="submit"
                                        data-testid="form-submit-btn"
                                        disabled={sending}
                                        className="group relative w-full overflow-hidden rounded-full bg-[#F58220] text-white font-semibold py-4 text-sm transition-all duration-300 hover:shadow-[0_0_0_8px_rgba(245,130,32,0.15)] disabled:opacity-60"
                                    >
                                        <span className="absolute inset-0 bg-[#0E1E12] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                                        <span className="relative flex items-center justify-center gap-2">
                                            {sending ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                                                </>
                                            ) : (
                                                <>
                                                    Request my free machine <Send className="h-4 w-4" />
                                                </>
                                            )}
                                        </span>
                                    </button>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
